import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowDown, FaArrowRotateRight, FaArrowUp, FaCircleInfo, FaMinus } from "react-icons/fa6";
import { css } from "../../../../styled-system/css";
import { Box, Flex, Grid } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import Card from "../../../components/core/basic/Card";
import { type Audience, type Comparison, daysSince, percentChange, type ReportSummary } from "../model";
import { TEAM_COLOR, TRELLIS_COLOR } from "../theme";
import { Sparkline, useCount, useRelativeTime } from "./primitives";

export const PERIOD_OPTIONS = ["7", "30", "90"] as const;
export type PeriodOption = (typeof PERIOD_OPTIONS)[number];

export function ReportHeader({
  orgName,
  days,
  onDaysChange,
  audience,
  onAudienceChange,
  generatedAt,
  isFetching,
  onRefresh,
}: {
  orgName: string | undefined;
  days: PeriodOption;
  onDaysChange: (days: PeriodOption) => void;
  audience: Audience;
  onAudienceChange: (audience: Audience) => void;
  generatedAt: string | undefined;
  isFetching: boolean;
  onRefresh: () => void;
}) {
  const { t } = useTranslation(["user_activity"]);
  const relativeTime = useRelativeTime();

  return (
    <Flex direction="column" gap="medium">
      <Flex justifyContent="space-between" alignItems="flex-end" gap="medium" flexWrap="wrap">
        <Box>
          <p
            className={css({
              margin: 0,
              color: "text.secondary",
              fontSize: "small",
              fontWeight: "600",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            })}
          >
            {t(($) => $.user_activity["Staff tools · Team activity"])}
          </p>
          <h1 className={css({ margin: 0, color: "text.heading", fontSize: "28px", fontWeight: "700" })}>
            {orgName ?? " "}
          </h1>
          <p className={css({ margin: 0, color: "text.secondary", fontSize: "medium" })}>
            {t(($) => $.user_activity["Who is using Trellis, what they do, and what they're missing."])}
          </p>
        </Box>
        <Flex alignItems="center" gap="small" color="text.secondary" fontSize="small">
          {generatedAt && (
            <span>{t(($) => $.user_activity["Updated {when}"], { when: relativeTime(new Date(generatedAt)) })}</span>
          )}
          <Button
            variant="icon"
            onPress={onRefresh}
            isDisabled={isFetching}
            aria-label={t(($) => $.user_activity["Refresh"])}
            tooltip={t(($) => $.user_activity["Refresh"])}
          >
            <FaArrowRotateRight className={isFetching ? css({ animation: "spin" }) : undefined} />
          </Button>
        </Flex>
      </Flex>
      <Flex gap="medium" flexWrap="wrap" alignItems="center">
        <ButtonGroup<PeriodOption>
          value={days}
          onSelect={onDaysChange}
          buttons={[
            { value: "7", label: t(($) => $.user_activity["Last 7 days"]) },
            { value: "30", label: t(($) => $.user_activity["Last 30 days"]) },
            { value: "90", label: t(($) => $.user_activity["Last 90 days"]) },
          ]}
        />
        <ButtonGroup<Audience>
          value={audience}
          onSelect={onAudienceChange}
          buttons={[
            { value: "team", label: t(($) => $.user_activity["Customer team"]) },
            { value: "trellis", label: t(($) => $.user_activity["Trellis team"]) },
            { value: "everyone", label: t(($) => $.user_activity["Everyone"]) },
          ]}
        />
      </Flex>
    </Flex>
  );
}

function Trend({ comparison, days }: { comparison: Comparison; days: number }) {
  const { t } = useTranslation(["user_activity"]);
  const count = useCount();
  const change = percentChange(comparison);
  const direction =
    comparison.current > comparison.previous ? "up"
    : comparison.current < comparison.previous ? "down"
    : "flat";

  const text =
    change === null ?
      comparison.current === 0 ?
        t(($) => $.user_activity["No activity either period"])
      : t(($) => $.user_activity["New this period"])
    : direction === "flat" ? t(($) => $.user_activity["Same as previous {days} days"], { days })
    : t(($) => $.user_activity["{change} vs previous {days} days"], {
        change: count.percent(Math.abs(change)),
        days,
      });

  const color =
    direction === "up" ? "#006300"
    : direction === "down" ? "#b42318"
    : "#5c566b";
  const icon =
    direction === "up" ? <FaArrowUp />
    : direction === "down" ? <FaArrowDown />
    : <FaMinus />;

  return (
    <span className={css({ display: "inline-flex", gap: "4px", alignItems: "center", fontSize: "small" })}>
      <span style={{ color, display: "inline-flex" }} aria-hidden>
        {icon}
      </span>
      <span style={{ color }}>{text}</span>
    </span>
  );
}

function Tile({
  label,
  value,
  context,
  trend,
  sparkline,
}: {
  label: string;
  value: string;
  context?: ReactNode;
  trend: ReactNode;
  sparkline?: ReactNode;
}) {
  return (
    <Card css={{ display: "flex", flexDirection: "column", gap: "xsmall", padding: "large", borderRadius: "standard" }}>
      <span className={css({ color: "text.secondary", fontSize: "medium", fontWeight: "500" })}>{label}</span>
      <Flex alignItems="flex-end" justifyContent="space-between" gap="small">
        <span className={css({ color: "text.heading", fontSize: "34px", fontWeight: "700", lineHeight: "1.1" })}>
          {value}
          {context && (
            <span className={css({ marginLeft: "6px", color: "text.secondary", fontSize: "large", fontWeight: "500" })}>
              {context}
            </span>
          )}
        </span>
        {sparkline}
      </Flex>
      {trend}
    </Card>
  );
}

export function KpiTiles({ summary, audience }: { summary: ReportSummary; audience: Audience }) {
  const { t } = useTranslation(["user_activity"]);
  const count = useCount();
  const days = summary.days.length;
  const color = audience === "trellis" ? TRELLIS_COLOR : TEAM_COLOR;
  const sparkline = (values: number[], label: string) => (
    <Sparkline values={values} color={color} width={96} height={34} label={label} />
  );

  return (
    <Grid gridTemplateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" }} gap="medium">
      <Tile
        label={t(($) => $.user_activity["People using Trellis"])}
        value={count.whole(summary.activePeople.current)}
        context={t(($) => $.user_activity["of {total}"], { total: summary.peopleWithAccess })}
        trend={<Trend comparison={summary.activePeople} days={days} />}
        sparkline={sparkline(
          summary.daily.map((point) => point.activePeople),
          t(($) => $.user_activity["People using Trellis each day"]),
        )}
      />
      <Tile
        label={t(($) => $.user_activity["Days active per person"])}
        value={count.oneDecimal(summary.activeDaysPerPerson.current)}
        context={t(($) => $.user_activity["of {days} days"], { days })}
        trend={<Trend comparison={summary.activeDaysPerPerson} days={days} />}
      />
      <Tile
        label={t(($) => $.user_activity["Changes made"])}
        value={count.whole(summary.changes.current)}
        trend={<Trend comparison={summary.changes} days={days} />}
        sparkline={sparkline(
          summary.daily.map((point) => point.changes),
          t(($) => $.user_activity["Changes made each day"]),
        )}
      />
      <Tile
        label={t(($) => $.user_activity["Pages viewed"])}
        value={count.whole(summary.views.current)}
        trend={<Trend comparison={summary.views} days={days} />}
        sparkline={sparkline(
          summary.daily.map((point) => point.views),
          t(($) => $.user_activity["Pages viewed each day"]),
        )}
      />
    </Grid>
  );
}

/** The report in three plain sentences, for someone who will not read the charts. */
export function SummaryCallout({
  summary,
  audience,
  unavailableSources,
}: {
  summary: ReportSummary;
  audience: Audience;
  unavailableSources: string[];
}) {
  const { t } = useTranslation(["user_activity"]);
  const days = summary.days.length;
  const { current, previous } = summary.activePeople;

  const who =
    audience === "trellis" ? t(($) => $.user_activity["Trellis staff"])
    : audience === "everyone" ? t(($) => $.user_activity["people"])
    : t(($) => $.user_activity["people on the customer's team"]);

  const sentences: string[] = [
    t(($) => $.user_activity["{active} of {total} {who} used Trellis in the last {days} days."], {
      active: current,
      total: summary.peopleWithAccess,
      who,
      days,
    }),
  ];
  if (current > previous) {
    sentences.push(t(($) => $.user_activity["That's up from {previous} the period before."], { previous }));
  } else if (current < previous) {
    sentences.push(t(($) => $.user_activity["That's down from {previous} the period before."], { previous }));
  }

  const changes = summary.changes.current;
  if (changes > 0 && summary.topChangeKind === "campaign_change") {
    sentences.push(t(($) => $.user_activity["They made {changes} changes, mostly to campaigns."], { changes }));
  } else if (changes > 0 && summary.topChangeKind === "keyword_change") {
    sentences.push(
      t(($) => $.user_activity["They made {changes} changes, mostly to keywords and targets."], { changes }),
    );
  } else if (changes > 0) {
    sentences.push(t(($) => $.user_activity["They made {changes} changes, mostly to rules."], { changes }));
  } else {
    sentences.push(t(($) => $.user_activity["Nobody changed a campaign, keyword or rule."]));
  }

  const away = summary.longestAway;
  if (away) {
    sentences.push(
      away.lastSeen ?
        t(($) => $.user_activity["{name} hasn't been seen for {days} days."], {
          name: away.person.name,
          days: daysSince(away.lastSeen, new Date()),
        })
      : t(($) => $.user_activity["{name} has access but has never logged in."], { name: away.person.name }),
    );
  }

  return (
    <Card
      css={{
        display: "flex",
        gap: "medium",
        alignItems: "flex-start",
        borderRadius: "standard",
        padding: "large",
        background: "linear-gradient(120deg, #f7f3fc 0%, #fdf1f6 100%)",
        boxShadow: "none",
      }}
    >
      <Box
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="pill"
        width="40px"
        height="40px"
        color="white"
        fontSize="large"
        style={{ backgroundColor: TEAM_COLOR }}
        aria-hidden
      >
        <FaCircleInfo />
      </Box>
      <Box>
        <p
          className={css({ margin: 0, color: "text.heading", fontSize: "large", fontWeight: "500", lineHeight: "1.6" })}
        >
          {sentences.join(" ")}
        </p>
        {unavailableSources.length > 0 && (
          <p className={css({ margin: 0, marginTop: "small", color: "text.secondary", fontSize: "small" })}>
            {t(
              ($) =>
                $.user_activity[
                  "Some activity took too long to load and is missing from this report. Try refreshing in a minute."
                ],
            )}
          </p>
        )}
      </Box>
    </Card>
  );
}
